/**
 * caseDoctor.js — WHO IS THE DOCTOR ON THIS CASE?
 * v242.175.0 · BUILD123-REINSTATE-1 (2 of 3), FU-SCHEDULE-DOCTORREF-1
 *
 * ★★★ HIS REPORT, 2026-08-22: "scheduling for follow up is broken now" … "of course it
 * has an MD assigned its a follow up visit!" v242.123.0 fixed it and was withdrawn whole
 * during the DELIVER-AGENT-OPENER-2 revert chain, for a defect in a different file.
 * Re-measured 2026-09-08: still exactly as filed.
 *
 * ★★★ THE SCREEN ASKED ONE FIELD AND IT WAS THE WRONG ONE. `sentToDoctor` is the
 * SHARE-FLOW column — written when a patient picks an Orella doctor and sends their case.
 * A FOLLOW-UP's doctor reaches the case through the APPOINTMENT and the ENCOUNTER, so
 * that column is empty on a case that plainly has a doctor, and
 * `if (!doctorId) setAvailabilityEnabled(false)` fired before a single API call. The
 * patient was told their doctor has no online scheduling, on a follow-up their doctor
 * booked.
 *
 * ★★★ AND THE SAME DEAD READ COST THE PHONE NUMBER. "Call the office" read
 * `sentToDoctorData.phone`, so on the very case that could not book online, the one
 * remaining affordance was withheld too. ONE BAD READ, TWO MISSING THINGS.
 *
 * ★★ THE CHAIN IS NOT NEW — IT IS DeliverScreen's OWN, which has resolved the doctor three
 * ways since v125.0.7: the share record, then the appointment, then the case field. One
 * screen resolved it three ways and the screen it opens resolved it one way. That is the
 * defect stated as a sentence, and it is why this is an OWNER rather than a second copy
 * pasted into the scheduler.
 *
 * ★ `formatAddress` is imported rather than passed: `practice_address` arrives as a JSONB
 *   dict, a JSON string or a plain string depending on the route, and a caller that
 *   forgets which is a caller rendering "[object Object]" to a patient.
 */
import { formatAddress } from './formatAddress.js'
import { isOutsideAppointment } from './outsideAppointment.js' // PROD-1160

/**
 * The doctor's ID, for anything that has to ASK THE SERVER about them — availability,
 * slots, a profile fetch.
 * ★★ Returns null only when the case genuinely names nobody. That is the answer
 *   `setAvailabilityEnabled(false)` was being given for every follow-up.
 */
export function caseDoctorId(caseData) {
  const direct = caseData?.sentToDoctor || caseData?.sent_to_doctor
  if (direct) return direct
  const apt = (caseData?.appointments || []).find((a) => !isOutsideAppointment(a) && (a?.doctor_id || a?.doctorId))
  if (apt) return apt.doctor_id || apt.doctorId
  const enc = (caseData?.encounters || []).find((e) => e?.doctor_id || e?.doctorId)
  if (enc) return enc.doctor_id || enc.doctorId
  // ★ `case.doctor` — measured 2026-09-08: SEVEN screens pair `sentToDoctorData` with this
  //   object, so a chain that does not read it is narrower than the screens it replaces.
  const obj = caseData?.doctor
  if (obj?.id || obj?.doctor_id) return obj.id || obj.doctor_id
  return caseData?.doctorId || caseData?.doctor_id || null
}

/**
 * The doctor as something to DISPLAY — name, practice, phone, photo.
 * Same three sources in the same order, so the id and the record can never disagree
 * about which doctor this case is about.
 */
export function caseDoctor(caseData) {
  const apt = (caseData?.appointments || []).find((a) => !isOutsideAppointment(a) && (a?.doctor_first_name || a?.doctor_name))
  const aptPhoto = apt?.doctor_photo || null

  // Primary: the share-flow record. Backfill the photo from the appointment when the
  // share record lacks one (v225.23.0, Bug 1).
  if (caseData?.sentToDoctorData?.first_name) {
    const std = caseData.sentToDoctorData
    return { ...std, profile_photo: std.profile_photo || aptPhoto }
  }
  if (apt) {
    return {
      id: apt.doctor_id || apt.doctorId || null,
      first_name: apt.doctor_first_name || apt.doctor_name?.split(' ')[1] || '',
      last_name: apt.doctor_last_name || apt.doctor_name?.split(' ').slice(2).join(' ') || '',
      // v225.11.0 (#9): specialty comes from the doctor/appointment record only — never
      // from caseData.specialtyName, which is the case's specialty AREA, e.g. "Ear".
      // ★ BOTH SPELLINGS, DELIBERATELY. The share record carries `specialty` and
      //   DeliverScreen reads `specialties`; a record that answers only one of them
      //   makes the caller guess, and a guessing caller is the defect this file exists
      //   to end.
      specialty: apt.specialty || apt.doctor_specialty || '',
      specialties: (apt.specialty || apt.doctor_specialty) ? [(apt.specialty || apt.doctor_specialty)] : [],
      practice_name: apt.practice_name || '',
      // ★★★ v242.175.0 — THE CITY WAS BEING WRITTEN TWICE. This branch used to pre-join
      //   city and state INTO practice_address, while every consumer joins them itself:
      //   DoctorMiniCard builds `[practice_address, city, state, zip]` and the scheduler's
      //   card renders `{formatAddress(practice_address)}, {city}, {state} {zip}`. On an
      //   appointment-sourced doctor that rendered "1 Main St, Austin, TX, Austin, TX" to a
      //   patient. The share-record branch never did this, and the whole point of an owner
      //   is that its two branches hand back the SAME SHAPE.
      practice_address: formatAddress(apt.practice_address),
      practice_city: apt.practice_city || '',
      practice_state: apt.practice_state || '',
      practice_zip: apt.practice_zip || '',
      practice_phone: apt.practice_phone || '',
      profile_photo: apt.doctor_photo || null,
    }
  }
  // The case-level doctor object, when the API supplied one.
  if (caseData?.doctor?.first_name || caseData?.doctor?.last_name) {
    const d = caseData.doctor
    return { ...d, profile_photo: d.profile_photo || aptPhoto }
  }
  // Last resort: the case-level name string.
  const name = caseData?.sentToDoctorName || ''
  const match = name.match(/Dr\.\s+(\w+)\s+(.+)/)
  if (match) return { first_name: match[1], last_name: match[2], specialties: [] }
  return null
}

/** The number to call, wherever it came from. Null when there is none — see below. */
export function caseDoctorPhone(caseData) {
  const d = caseDoctor(caseData)
  return d?.phone || d?.practice_phone || null
}
